import { Icon } from '@latitude/icons';
import type { ChangeEvent, FocusEvent, MouseEvent } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { DatepickerInputProps } from 'types';
import type { ModeValue } from 'utils/dateUtils';
import { formatValue, isValueEqual } from 'utils/dateUtils';
import {
  isIncompleteInput,
  validateDateInput,
  validateDateRangeInput,
} from 'utils/validation';

const VALIDATION_DEBOUNCE_MS = 500;

type ParsedText =
  | { ok: true; value: ModeValue; canonicalText?: string }
  | { ok: false; incomplete: boolean; errorMessage: string | undefined };

type ParseOptions = {
  selection: ModeValue;
  format: string;
  minDate: Date;
  maxDate: Date;
};

const parseText = (
  input: string,
  strictMode: boolean,
  { selection, format, minDate, maxDate }: ParseOptions,
): ParsedText => {
  const config = { format, minDate, maxDate, strictMode };
  if (selection.mode === 'range') {
    const result = validateDateRangeInput(input, config);
    return result.isValid && result.parsedRange
      ? {
          ok: true,
          value: { mode: selection.mode, value: result.parsedRange },
        }
      : {
          ok: false,
          incomplete: result.errorType === 'INCOMPLETE',
          errorMessage: result.errorMessage,
        };
  }
  const result = validateDateInput(input, config);
  return result.isValid && result.parsedDate
    ? {
        ok: true,
        value: { mode: selection.mode, value: result.parsedDate },
        canonicalText: result.correctedValue,
      }
    : {
        ok: false,
        incomplete: result.errorType === 'INCOMPLETE',
        errorMessage: result.errorMessage,
      };
};

type UseDatepickerInputOptions = ParseOptions & {
  commit: (next: ModeValue) => void;
  openCalendar: () => void;
  disabled: boolean;
  readOnly: boolean;
  required: boolean;
  placeholder: string;
  id?: string;
  testId?: string;
  ariaLabel?: string;
  onClear?: () => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
};

/**
 * The text field: what the user typed, its validation error, and how typed
 * text becomes a committed value (debounced while typing, strict on blur).
 */
export const useDatepickerInput = ({
  selection,
  format,
  minDate,
  maxDate,
  commit,
  openCalendar,
  disabled,
  readOnly,
  required,
  placeholder,
  id,
  testId,
  ariaLabel,
  onClear,
  onBlur,
}: UseDatepickerInputOptions) => {
  const [text, setText] = useState(() => formatValue(selection, format));
  const [errorMessage, setErrorMessage] = useState<string>();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // The value `text` was last written for. If the committed value moves
  // another way (a parent update), the text is rewritten; values committed
  // from typing are recorded here first, so typed text is kept.
  const [textValue, setTextValue] = useState(selection);
  if (!isValueEqual(textValue, selection)) {
    setTextValue(selection);
    setText(formatValue(selection, format));
    setErrorMessage(undefined);
  }
  const empty: ModeValue = { ...selection, value: undefined };

  // The debounce timer fires renders after its keystroke, so it reads props
  // from here rather than from the closure it was created in.
  const latest = useRef({ text, selection, format, minDate, maxDate, commit });
  useLayoutEffect(() => {
    latest.current = { text, selection, format, minDate, maxDate, commit };
  });

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const commitFromText = (next: ModeValue, canonicalText?: string) => {
    setTextValue(next);
    setErrorMessage(undefined);
    if (canonicalText) {
      setText(canonicalText);
    }
    latest.current.commit(next);
  };

  // Text that can't become a date leaves no date behind, so a form can't keep,
  // and submit, the date the user typed over. The text and error stay.
  const rejectText = (message: string) => {
    const { selection: current, commit: commitValue } = latest.current;
    if (current.value !== undefined) {
      const cleared: ModeValue = { ...current, value: undefined };
      setTextValue(cleared);
      commitValue(cleared);
    }
    setErrorMessage(message);
  };

  /**
   * Shows `next` in the field and drops any typed text, error or pending
   * commit. For values committed outside the text field.
   */
  const syncText = (next: ModeValue) => {
    clearTimeout(debounceRef.current);
    setTextValue(next);
    setText(formatValue(next, format));
    setErrorMessage(undefined);
  };

  // Time is set with the spinners. The native readOnly would also hide the
  // input's clear button, so edits are cancelled before they land instead.
  const isTimeMode = selection.mode === 'time';
  const blockTyping = useCallback(
    (node: HTMLInputElement | null) => {
      if (!node || !isTimeMode) {
        return;
      }
      const cancel = (event: Event) => event.preventDefault();
      node.addEventListener('beforeinput', cancel);
      return () => node.removeEventListener('beforeinput', cancel);
    },
    [isTimeMode],
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const typed = event.target.value;
    setText(typed);
    setErrorMessage(undefined);
    // An edit can still land in time mode: beforeinput is not cancelable
    // during IME composition. It is shown until blur, never committed.
    if (isTimeMode) {
      return;
    }

    const typedOver = selection;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const current = latest.current;
      // A value set elsewhere since the keystroke, such as a form reset, wins.
      // It rewrites the text, so this also catches a value set and set back.
      if (
        current.text !== typed ||
        !isValueEqual(current.selection, typedOver)
      ) {
        return;
      }
      if (!typed.trim()) {
        commitFromText({ ...current.selection, value: undefined });
        return;
      }
      if (isIncompleteInput(typed, current.format)) {
        return;
      }
      const parsed = parseText(typed, false, current);
      if (parsed.ok) {
        commitFromText(parsed.value, parsed.canonicalText);
      } else if (!parsed.incomplete && parsed.errorMessage) {
        rejectText(parsed.errorMessage);
      }
    }, VALIDATION_DEBOUNCE_MS);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    // Focus moving into the calendar is part of the same edit: a day click
    // blurs the input before it commits, and reporting that blur would make
    // react-hook-form validate the old value.
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget?.closest('[data-testid="datepicker-popover"]')) {
      return;
    }
    clearTimeout(debounceRef.current);

    // Only an edit is validated. Untouched text already shows the committed
    // value, which may sit outside the bounds or, in time mode, on another day.
    if (text === formatValue(selection, format)) {
      onBlur?.(event);
      return;
    }

    if (isTimeMode) {
      syncText(selection);
      onBlur?.(event);
      return;
    }

    const typed = text.trim();
    if (!typed) {
      if (selection.value !== undefined) {
        commitFromText(empty);
      }
      setErrorMessage(undefined);
      onBlur?.(event);
      return;
    }

    const parsed = parseText(typed, true, {
      selection,
      format,
      minDate,
      maxDate,
    });
    if (parsed.ok && isValueEqual(parsed.value, selection)) {
      // Text such as a start date alone parses to the committed range without
      // matching its formatted text; committing again would report a change.
      setErrorMessage(undefined);
    } else if (parsed.ok) {
      commitFromText(parsed.value, parsed.canonicalText);
    } else {
      rejectText(
        parsed.errorMessage ||
          (selection.mode === 'range' ? 'Invalid date range' : 'Invalid date'),
      );
    }
    onBlur?.(event);
  };

  const hasError = errorMessage !== undefined;

  const inputProps: DatepickerInputProps = {
    ref: blockTyping,
    disabled,
    readOnly,
    value: text,
    error: hasError,
    trailingElement: (
      <Icon name="calendar" className="px-spacing-sys-spacing-2" />
    ),
    classNames: {
      input: 'flex justify-start',
    },
    placeholder: placeholder || 'Pick a date',
    autoComplete: 'off',
    'data-testid': testId || 'datepicker-input',
    id,
    'aria-haspopup': 'dialog',
    rawProps: {
      'aria-label': ariaLabel,
      'aria-required': required || undefined,
    },
    // preventDefault leaves the text to the datepicker, so Input neither
    // blanks it nor reports an empty change, and makes the popover trigger
    // the button sits in skip its open toggle.
    onClear: (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      syncText(empty);
      commit(empty);
      onClear?.();
    },
    onClick: (event: MouseEvent<HTMLInputElement>) => {
      event.preventDefault();
      event.stopPropagation();
      openCalendar();
    },
    onChange: handleChange,
    onBlur: handleBlur,
  };

  return { inputProps, errorMessage, syncText };
};
